/** ****************************************************************************
 * This constants file has constants for logging strings.
 *
 * Logging information has 3 classifications in this project.
 * They are: information, warning, and error.
 * Each of the classifications has a numbering scheme:
 * -- Informational: 1xxx
 * -- Warning: 2xxx
 * -- Errors: 3xxx-9xxx
 *
 * @file   Constants file, various strings for logs.
 * @author Paul J Stales
 * @since  2.0.0
 **************************************************************************** */

/**
 * INFORMATION does not affect the integration at all.
 * Showing these are optional, as they do not affect anything.
 *
 * These informational messages are used when there is no need for a warning nor an error.
 * They may even be hidden, and saved for debugging purposes.
 *
 * Whether or not to show this information will remain a business decision.
 * Inside the validation files will be a SHOW_INFO_FLAG that controls this.
 *
 * Note that all information messages are numbered in the 1000s.
 */
export const INFO_MESSAGES = {
  INFO_1001_CUSTOMIZATIONS_OMITTED: `[BPERX INFO 1001 - CUSTOMIZATIONS_OMITTED]
    The PartnerSuppliedInfo did not have an optional partner customizations object.
    Defaults will be chosen for any possible customizations.`,
  INFO_1002_CUSTOM_CSS_OMITTED: `[BPERX 1NFO 1002 - CUSTOM_CSS_OMITTED]
    The PartnerSuppliedInfo's partnerCustomizations object omitted the optional usesCustomCSS.
    The value will default to FALSE, default bPerx CSS will be used.
    For information about custom CSS, talk to your bPerx associate.`,
  INFO_1003_IS_CUSTOMER_DEFAULT_OPTING_IN_OMITTED: `[BPERX INFO 1003 - IS_CUSTOMER_DEFAULT_OPTING_IN_OMITTED]
    The PartnerSuppliedInfo's partnerCustomizations object omitted the optional defaultOptingIn.
    The value will default to TRUE, customers will be opted in by default.`,
  INFO_1004_OPT_ACTION_ELEMENT_ALWAYS_VISIBLE_OMITTED: `[BPERX INFO 1004 - IS_OPT_ACTION_ELEMENT_ALWAYS_VISIBLE_OMITTED]
    The PartnerSuppliedInfo's partnerCustomizations object omitted the optional isOptElementAlwaysVisible.
    The value will default to FALSE, opt checkbox is visible only when offers are visible.`,
  INFO_1005_WANTS_DIGITAL_READING_LINKS_OMITTED: `[BPERX INFO 1005 - WANTS_DIGITAL_READING_LINKS_OMITTED]
    The PartnerSuppliedInfo's partnerCustomizations object omitted the optional wantsDigitalReadingLinks.
    The value will default to FALSE, and the partner website will NOT have any digital reading links displayed.`,
  INFO_1006_IS_ACTION_ELEMENT_POSITIVE_INTENT_TO_OPT_IN_OMITTED: `[BPERX INFO 1006 - IS_ACTION_ELEMENT_POSITIVE_INTENT_TO_OPT_IN_OMITTED]
    The PartnerSuppliedInfo's partnerCustomizations object omitted the optional isIntentToOptIn.
    The value will default to TRUE, and the partner website will consider any "1" position (e.g. checked checkbox") to
    indicate that the user is opting in.`,
  INFO_2001_PARTNER_NOT_ENABLED_EVENT_NOT_EMITTED: `[BPERX INFO 2001 - PARTNER_NOT_ENABLED_EVENT_NOT_EMITTED]
    The current partner ID is not enabled for custom reporting. No custom reporting event will be emitted.`,
  INFO_2002_PARTNER_ENABLED_EVENT_EMITTED: `[BPERX INFO 2002 - PARTNER_ENABLED_EVENT_EMITTED]
    The current partner ID is enabled for custom reporting. A custom reporting event will be emitted.`,
  INFO_2003_PARTNER_ADDED_CUSTOM_EVENT_CALLBACK: `[BPERX INFO 2003 - PARTNER_ADDED_CUSTOM_EVENT_CALLBACK]
    Partner added a callback for a custom event, event name shown below.`
}

/**
 * WARNINGS do affect the integration in that we make minor corrections.
 * Showing these are required, as the partner's intended choice may have been changed.
 *
 * These warning messages are less-serious infractions.
 * Processing can continue with minor fixes, but the warning informs the partner of
 * their minor error and our corrective action taken.
 *
 * Note that all warning messages are numbered in the 2000s.
 */
export const WARN_MESSAGES = {
  WARN_2001_CUSTOMIZATIONS_INVALID: `[BPERX WARN 2001 - CUSTOMIZATIONS_INVALID]
    The PartnerSuppliedInfo's has an optional partner customizations object,
    but the value is invalid (either not an object, or does not have correct properties.)
    Please refer to the bPerx documentation (or ask a bPerx associate for help).
    Defaults will be chosen for any possible customizations.`,
  WARN_2002_USES_CUSTOM_CSS_NON_STRING: `[BPERX WARN 2002 - USES_CUSTOM_CSS_NON_STRING]
    The PartnerSuppliedInfo's partnerCustomizations object has usesCustomCSS, but it is not a string.
    The value will be corrected to FALSE, default bPerx CSS will be used.`,
  WARN_2003_IS_CUSTOMER_DEFAULT_OPTING_IN_NON_BOOLEAN: `[BPERX WARN 2003 - IS_CUSTOMER_DEFAULT_OPTING_IN_NON_BOOLEAN]
    The PartnerSuppliedInfo's partnerCustomizations object has defaultOptingIn, but a non-boolean value.
    The value will be corrected to TRUE, customers will be opted in by default.`,
  WARN_2004_IS_OPT_ACTION_ELEMENT_ALWAYS_VISIBLE_NON_BOOLEAN: `[BPERX WARN 2004 - IS_OPT_ACTION_ELEMENT_ALWAYS_VISIBLE_NON_BOOLEAN]
    The PartnerSuppliedInfo's partnerCustomizations object has isOptElementAlwaysVisible, but a non-boolean value.
    The value will be corrected to FALSE, opt checkbox is visible only when offers are visible.`,
  WARN_2005_WANTS_DIGITAL_READING_LINKS_NON_BOOLEAN: `[BPERX WARN 2005 - WANTS_DIGITAL_READING_LINKS_NON_BOOLEAN]
  The PartnerSuppliedInfo's partnerCustomizations object has wantsDigitalReadingLinks, but it is not a boolean.
  The value will default to FALSE, and the partner website will NOT have any digital reading links displayed.`,
  WARN_2006_IS_ACTION_ELEMENT_POSITIVE_INTENT_TO_OPT_IN_NON_BOOLEAN: `[BPERX WARN 2006 - IS_ACTION_ELEMENT_POSITIVE_INTENT_TO_OPT_IN]
  The PartnerSuppliedInfo's partnerCustomizations object has isIntentToOptIn, but it is not a boolean.
  The value will default to TRUE, and the partner website will consider any "1" position (e.g. checked checkbox") to
  indicate that the user is opting in.`,
  WARN_2101_BPERX_REPORTABLE_EVENT_NOT_FOUND: `[BPERX WARN ERROR 2101 - UNRECOGNIZED_BPERX_REPORTABLE_EVENT]:
    iFrame attempted to send the partner a reportable event, but the reportable event was unrecognized.
    If your integration relies on custom reporting, report this to bPerx.`
}

/**
 * ERRORS affect the integration so much, that we fail the entire integration.
 * Showing these are required, as the integration fails.
 *
 * These error messages are serious errors.
 * We cannot make any reasonable corrections, and the script should stop running.
 * We inform the partner the integration has failed.
 *
 * IMPORTANT: Never throw an exception - we do NOT want to stop the partner's JS.
 * We merely want to stop OUR processing.
 *
 * Note that all errors messages are numbered in the 3000s and above,
 * where every set of 1000 is a "type" of error.
 *
 * 4000s - related to the transaction information object
 * 5000s - related to the iframe postmessage
 * 6000s - related to calling the bperx api
 * 7000s - related to reportable events
 */
export const ERROR_MESSAGES = {
  INTEGRATION_FAILED: `[BPERX IFRAME INTEGRATION FAILED]
    The bPerx iFrame integration has failed due to one or more errors.
    The integration will not be able to display, and will close.
    Please check the console error messages and make corrections.
    Please refer to the bPerx Iframe integration page (or ask a bPerx associate for help).
    If you need further assistance, contact the bPerx team.`,
  VALIDATIONS_FAILED: `[PARTNER-SUPPLIED DATA FAILED VALIDATION]
    One or more parts of the partner-supplied data failed validation.
    Carefully read the numbered, specific error message for more details.`,
  ERROR_3001_LOCAL_STORAGE_NOT_AVAILABLE: `[BPERX INTEGRATION ERROR 3001 - LOCALSTORAGE_NOT_AVAILABLE]:
    Local storage is not available. The iFrame cannot work without access to localstorage.
    The bPerx offer transaction cannot continue.`,
  ERROR_3002_INFORMATION_NOT_IN_LOCAL_STORAGE: `[BPERX INTEGRATION ERROR 3002 - INFORMATION_NOT_IN_LOCAL_STORAGE]:
    Local storage is available, but transaction information cannot be found despite formerly being available.
    The data may have been deleted by the user, the browser, or other means outside of bPerx control.
    The bPerx offer transaction cannot continue.`,
  ERROR_3011_NON_PARSABLE_DATA_IN_LOCAL_STORAGE: `[BPERX INTEGRATION ERROR 3012 - NON_PARSABLE_DATA_IN_LOCAL_STORAGE]:
    Local storage is available, transaction information was found, but the data cannot be correctly parsed.
    The data may have been changed by the user, the browser, or other means outside of bPerx control.
    The bPerx offer transaction cannot continue.`,
  ERROR_4001_PARTNER_SUPPLIED_OBJECT_MISSING: `[BPERX INTEGRATION ERROR 4001 - PARTNER_SUPPLIED_OBJECT_MISSING]:
    Partners must pass the PartnerSuppliedInfo to the display offer function.
    Please refer to the bPerx Iframe integration page for an explanation and example.`,
  ERROR_4002_PARTNER_SUPPLIED_OBJECT_NOT_OBJECT_TYPE: `[BPERX INTEGRATION ERROR 4002 - PARTNER_SUPPLIED_OBJECT_NOT_OBJECT_TYPE]:
    Partners must pass the PartnerSuppliedInfo, as an object type, to the display offer function.
    Please refer to the bPerx Iframe integration page for an explanation and example.`,
  ERROR_4011_REQUIRED_PROPERTY_IS_MISSING: `[BPERX INTEGRATION ERROR 4011 - REQUIRED_PROPERTY_MISSING]:
    Required property is missing: `,
  ERROR_4012_REQUIRED_PROPERTY_EMPTY: `[BPERX INTEGRATION ERROR 4012 - REQUIRED_PROPERTY_VALUE_EMPTY]:
    Property cannot be empty: `,
  ERROR_4013_REQUIRED_PROPERTY_UNDEFINED: `[BPERX INTEGRATION ERROR 4013 - REQUIRED_PROPERTY_VALUE_UNDEFINED]:
    Property cannot be undefined: `,
  ERROR_4014_REQUIRED_PROPERTY_NULL: `[BPERX INTEGRATION ERROR 4014 - REQUIRED_PROPERTY_VALUE_NULL]:
    Property cannot be null: `,
  ERROR_4015_REQUIRED_PROPERTY_INVALID_TYPE: `[BPERX INTEGRATION ERROR 4015 - REQUIRED_PROPERTY_INVALID_TYPE]:
    Property must be of type "string", property `,
  ERROR_4021_QUERY_SELECTOR_NOT_FOUND: `[BPERX INTEGRATION ERROR 4021 - QUERY_SELECTOR_NOT_FOUND]:
    An element with the given query selector cannot be found on your webpage.
    Possible problems:
    -- Are you missing the # for ID?
    -- Do you have a case-sensitive issue?
    -- Did you make a typo?
    We suggest trying these steps:
    1 In the element where you want the iFrame, give it an id "bperxbox".
    2 In the call to partnerDisplayOffer, the object passed in, the field "querySelector", write in "#bperxbox".
    The query selector given in the PartnerSuppliedInfo's FrameInformation object is: `,
  ERROR_4030_COUNTRY_CODE_INVALID: `[BPERX INTEGRATION ERROR 4030 - COUNTRY_CODE_INVALID]:
    Country code must be ISO-3166 alpha-2 or alpha-3 formatted. Support for only US and Canada at this time.`,
  ERROR_5001_MESSAGE_TYPE_MISSING: `[BPERX INTEGRATION ERROR 5001 - MESSAGE_TYPE_MISSING]:
    Messaging between the partner website and the bperx iframe has failed.
    The MESSAGE TYPE is missing from the IFramePostMessage data object.
    Please request assistance from bPerx.`,
  ERROR_5002_MESSAGE_TYPE_NOT_RECOGNIZED: `[BPERX INTEGRATION ERROR 5002 - MESSAGE_TYPE_NOT_RECOGNIZED]:
    Messaging between the partner website and the bperx iFrame has failed.
    The MESSAGE TYPE is present, but the MESSAGE TYPE is not recognized.
    Please request assistance from bPerx.`,
  ERROR_6001_BPERX_API_CALL_FAILED: `[BPERX INTEGRATION ERROR 6001 - BPERX_API_CALL_FAILED]:
    The API call to BPERX failed. The BPERX offer cannot be displayed.`,
  ERROR_6002_PROCESS_ENV_FAILED: `[BPERX INTEGRATION ERROR 6002 - PROCESS_ENV_FAILED]:
    Sorry for the inconvenience, BPERX servers are experiencing problems. Please inform your BPERX Representative.`
}

/**
 * DEV messages are for internal usage.
 */
export const D = {
  CUSTOMIZATIONS_AVAILABLE: 'CUSTOMIZATIONS: Available, checking prop-by-prop:',
  CUSTOMIZATIONS_NOTAVAIL_DISPLAY:
    'CUSTOMIZATIONS: Not available for display, using defaults.',
  CUSTOMIZATIONS_NOTAVAIL_REDEEM:
    'CUSTOMIZATIONS: Not available for redeem, using defaults.',
  IFRAME_DELETE_TI: 'IFRAME: Sending a deletion TI order to the partner',
  IFRAME_DIGITALLINK_FAILED: 'IFRAME: The digital popup cannot be loaded.',
  IFRAME_DIGITALLINK_SENT:
    'IFRAME: Sending a digital link to the partner website.',
  IFRAME_HANDLE_MESSAGE_RESPONSE: 'IFRAME: Handle message RESPONSE',
  IFRAME_ONMESSAGE_TRIGGERED: 'IFRAME: ONMESSAGE TRIGGERED',
  IFRAME_POSTMSG_SUPPRESSED: 'Suppressed preact/webpack configuration message.',
  REPORTABLE_EVENT: 'IFRAME: Sending a reportable event',
  IFRAME_REQUEST_PSI: 'IFRAME: Requesting PartnerSuppliedInfo from partner',
  IFRAME_REQUEST_TI:
    'IFRAME: Requesting TransactionInformation from partner (localstorage)',
  IFRAME_SEND_TI: 'IFRAME: Sending an update for the TransactionInformation',
  IFRAME_SEND_HEIGHT: 'IFRAME: Sending a height update',
  ONBOARD_STYLE: 'WEBSITE: Style for the header for the websites',
  PARTNER_MESSAGE_RECEIVED: 'PARTNER: Message received',
  PARTNER_RECEIVED_REQUEST_PSI:
    'PARTNER: Received request for partnerSuppliedInfo',
  PARTNER_RECEIVE_DELETE_TI:
    'PARTNER: Received delete for transactionInformation',
  PARTNER_RECEIVE_REPORTABLE_EVENT: 'PARTNER: Received a reportable event',
  PARTNER_RECEIVE_REQUEST_TI:
    'PARTNER: Received request for transactionInformation',
  PARTNER_RECEIVE_UPDATE_TI:
    'PARTNER: Received update for transactionInformation',
  PARTNER_SEND_RESPONSE_PSI:
    'PARTNER: Sending response with partnerSuppliedInfo',
  PARTNER_SEND_RESPONSE_TI:
    'PARTNER: Sending response with transactionInformation',
  PRODUCTS_API_CUSTOMER_FIRST_TIME_SET_TO_DEFAULT:
    'IFRAME: Products API call complete, customer choice not found, assigning choice to the product set default',
  PRODUCTS_API_CUSTOMER_CHOICE_EXPIRED_SET_TO_DEFAULT:
    'IFRAME: Products API call complete, customer choice expired (not in current set), assigning choice to the product set default',
  PRODUCTS_API_CUSTOMER_CHOICE_VALID:
    'IFRAME: Products API call complete, customer choice remains valid, returning',
  REDEMPTION_HTTP_REQUEST:
    'REDEEM: HTTP Request to send the redemption was sent to the bPerx API.',
  REDEMPTION_HTTP_RESPONSE_FAILURE:
    'REDEEM: HTTP Response from bPerx API, the redemption failed.',
  REDEMPTION_HTTP_RESPONSE_SUCCESS:
    'REDEEM: HTTP Response from bPerx API, the redemption succeeded',
  VOUCHER_GENERATED_TERMS:
    'Voucher is being generated as a result of a terms link click',
  VOUCHER_GENERATED_POST_ORDER:
    'Voucher is being generated right before a post order',
  VOUCHER_EXISTS_AT_POST_ORDER:
    'Voucher exists and is valid at time of post order',
  VOUCHER_MISSING:
    'VOUCHER SERVICE: Voucher is missing, a new voucher is needed.',
  VOUCHER_EXPIRED:
    'VOUCHER SERVICE: Voucher is expired, a new voucher is needed.',
  VOUCHER_PRESENT_AND_NOT_EXPIRED:
    'VOUCHER SERVICE: There is a valid, non-expired voucher for this offer.'
}
